import React, {
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import './TimePicker.css';

/** Hour (0–23) and minute → HH:mm (24h). */
export function formatTimeHHmm(hours, minutes) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export function parseTimeHHmm(str) {
    if (!str || !/^([01]\d|2[0-3]):([0-5]\d)$/.test(str)) return null;
    const [hs, ms] = str.split(':');
    return { h: parseInt(hs, 10), m: parseInt(ms, 10) };
}

function buildMinuteOptions(step) {
    const out = [];
    for (let m = 0; m < 60; m += step) {
        out.push(m);
    }
    return out;
}

/**
 * TimePicker — accessible time field with popover (value HH:mm, 24h).
 */
function TimePicker({
    id: idProp,
    name = 'time',
    value,
    onChange,
    locale,
    ariaLabelledBy,
    disabled = false,
    required = false,
    dir = 'ltr',
    className = '',
    /** First selectable hour (0–23). */
    startHour = 0,
    /** Last selectable hour (0–23). */
    endHour = 23,
    /** Minute increment (e.g. 15 → 00, 15, 30, 45). */
    minuteStep = 15,
}) {
    const { t } = useTranslation();
    const reactId = useId();
    const triggerId = idProp ?? `${reactId}-trigger`;
    const dialogId = `${triggerId}-dialog`;

    const triggerRef = useRef(null);
    const popoverRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 280 });
    const [draftHour, setDraftHour] = useState(null);
    const [draftMinute, setDraftMinute] = useState(null);

    const effectiveLocale = locale || 'en';

    const hours = useMemo(() => {
        const lo = Math.max(0, Math.min(23, startHour));
        const hi = Math.max(lo, Math.min(23, endHour));
        return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
    }, [startHour, endHour]);

    const minutes = useMemo(() => buildMinuteOptions(minuteStep), [minuteStep]);

    useEffect(() => {
        if (!open) return;
        const parsed = parseTimeHHmm(value);
        if (parsed) {
            const hIn = hours.includes(parsed.h) ? parsed.h : hours[0];
            const mSnap = minutes.reduce((prev, m) =>
                Math.abs(m - parsed.m) < Math.abs(prev - parsed.m) ? m : prev
            , minutes[0]);
            setDraftHour(hIn);
            setDraftMinute(minutes.includes(parsed.m) ? parsed.m : mSnap);
        } else {
            setDraftHour(null);
            setDraftMinute(null);
        }
    }, [open, value, hours, minutes]);

    const displayText = useMemo(() => {
        if (!value) return '';
        const parsed = parseTimeHHmm(value);
        if (!parsed) return value;
        const d = new Date();
        d.setHours(parsed.h, parsed.m, 0, 0);
        return d.toLocaleTimeString(effectiveLocale, {
            hour: 'numeric',
            minute: '2-digit',
        });
    }, [value, effectiveLocale]);

    const updatePosition = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const w = Math.max(rect.width, 300);
        const isRtl = dir === 'rtl';
        let left = isRtl ? rect.right - w : rect.left;
        left = Math.max(8, Math.min(left, window.innerWidth - w - 8));
        setCoords({
            top: rect.bottom + 8,
            left,
            width: w,
        });
    }, [dir]);

    useLayoutEffect(() => {
        if (!open) return;
        updatePosition();
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open) return;
        const onScroll = () => updatePosition();
        const onResize = () => updatePosition();
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
        };
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            const tgt = e.target;
            if (triggerRef.current?.contains(tgt) || popoverRef.current?.contains(tgt)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    const pickHour = (h) => {
        setDraftHour(h);
    };

    const pickMinute = (m) => {
        const h = draftHour ?? hours[0];
        if (h === undefined) return;
        onChange(formatTimeHHmm(h, m));
        setOpen(false);
    };

    const toggleOpen = () => {
        if (disabled) return;
        setOpen((o) => !o);
    };

    const rootClass = ['tp', className].filter(Boolean).join(' ');

    const popover = open
        ? createPortal(
              <div
                  ref={popoverRef}
                  id={dialogId}
                  className="tp-popover"
                  role="dialog"
                  aria-modal="false"
                  aria-label={t('reservation.time')}
                  dir={dir}
                  style={{
                      position: 'fixed',
                      top: coords.top,
                      left: coords.left,
                      width: coords.width,
                      zIndex: 10050,
                  }}
              >
                  <p className="tp-section-label" id={`${dialogId}-h`}>
                      {t('reservation.timePickerHours')}
                  </p>
                  <div
                      className="tp-hours"
                      role="group"
                      aria-labelledby={`${dialogId}-h`}
                  >
                      {hours.map((h) => (
                          <button
                              key={h}
                              type="button"
                              className={[
                                  'tp-cell',
                                  draftHour === h && 'tp-cell--active',
                              ]
                                  .filter(Boolean)
                                  .join(' ')}
                              onClick={() => pickHour(h)}
                          >
                              {String(h).padStart(2, '0')}
                          </button>
                      ))}
                  </div>
                  <p className="tp-section-label" id={`${dialogId}-m`}>
                      {t('reservation.timePickerMinutes')}
                  </p>
                  <div
                      className="tp-minutes"
                      role="group"
                      aria-labelledby={`${dialogId}-m`}
                  >
                      {minutes.map((m) => {
                          const hourReady = draftHour !== null;
                          return (
                              <button
                                  key={m}
                                  type="button"
                                  disabled={!hourReady}
                                  className={[
                                      'tp-cell',
                                      draftMinute === m && hourReady && 'tp-cell--active',
                                  ]
                                      .filter(Boolean)
                                      .join(' ')}
                                  onClick={() => pickMinute(m)}
                              >
                                  {String(m).padStart(2, '0')}
                              </button>
                          );
                      })}
                  </div>
              </div>,
              document.body
          )
        : null;

    return (
        <div className={rootClass}>
            <input
                type="text"
                name={name}
                value={value || ''}
                readOnly
                required={required}
                disabled={disabled}
                tabIndex={-1}
                aria-hidden="true"
                className="tp-sr-only"
            />
            <button
                ref={triggerRef}
                type="button"
                id={triggerId}
                className={`tp-trigger ${open ? 'tp-trigger--open' : ''}`}
                onClick={toggleOpen}
                disabled={disabled}
                aria-expanded={open}
                aria-haspopup="dialog"
                aria-controls={open ? dialogId : undefined}
                aria-labelledby={ariaLabelledBy || undefined}
                aria-label={!ariaLabelledBy ? t('reservation.timePickerOpen') : undefined}
            >
                <span className={value ? 'tp-trigger-text' : 'tp-trigger-text tp-trigger-text--placeholder'}>
                    {value ? displayText : t('reservation.timePickerPlaceholder')}
                </span>
                <span className="tp-trigger-ico" aria-hidden>
                    <ChevronDown size={18} strokeWidth={2} />
                </span>
            </button>
            {popover}
        </div>
    );
}

export default TimePicker;
